import { ActivityTracker } from '@company/pages/JobOverview/ActivityTracker';
import Messaging from '@company/pages/JobOverview/Messaging/Messaging';
import { SearchTalent } from '@company/pages/JobOverview/SearchTalent';
import Detail from '@company/pages/JobOverview/Detail/Detail';
import { useChatContext } from '@company/state/chatContext';
import { useJobContext } from '@company/state/jobContext';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Dropdown, Stack, Tab, Tabs } from 'react-bootstrap';
import { LoaderJobDetail } from '@components/elements/ComponentLoader';
import { Body } from '@company/components/layout';
import { SideEndModal, ThError } from '@components/elements';
import { FiChevronDown } from 'react-icons/fi';
import { isMobile } from 'react-device-detect';
import { useEffect, useState } from 'react';
import JobMessageChat from '@components/Chat/JobMessageChat';
import { BASE_PATH } from '@utils/Environment';
import { useQuery } from '@tanstack/react-query';
import * as chatApi from '@company/services/chat/api';
import QueryKeys from '@constants/queryKeys';
import DetailV2 from '@company/pages/JobOverview/DetailV2/DetailV2';
import { WorkerModal } from '@company/components/WorkerModal';
import { useJobDetailStore } from '@company/pages/JobOverview/DetailV2/stores/jobDetailStore';
import PayrollServices from './PayrollServices/PayrollServices';
import { Scheduling } from './Scheduling';
import './style.css';

const ENABLE_SUGGESTED_WORKERS =
  import.meta.env.VITE_ENABLE_SUGGESTED_WORKERS === 'true';

enum OverviewTabs {
  DETAILS = 'details',
  SEARCH_TALENTS = 'searchTalents',
  SCHEDULING = 'scheduling',
  ACTIVITY_TRACKER = 'activityTracker',
  MESSAGING = 'messaging',
  PAYROLL_SERVICES = 'payrollServices',
}

export default function JobOverview() {
  const { id, activeTab } = useParams();
  const { activeJobId, setJobParamId, isLoading, error, job } = useJobContext();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (activeJobId && activeJobId.toString() !== id) {
      navigate(`${BASE_PATH}jobs/${activeJobId}`);
    }
  }, [activeJobId, navigate, id]);

  useEffect(() => {
    if (id) {
      setJobParamId(id);
    }
  }, [id, setJobParamId]);

  const [key, setKey] = useState<string>(OverviewTabs.DETAILS);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [activitiesCount, setActivitiesCount] = useState(0);
  const [isTabValidated, setIsTabValidated] = useState(false);

  const {
    chatPerson,
    showPrivateInfo: showWorkerPrivateInfo,
    clearChatData,
  } = useChatContext();
  const selectedWorkerId = useJobDetailStore((state) => state.selectedWorkerId);
  const openWorkerModal = useJobDetailStore((state) => state.openWorkerModal);
  const closeWorkerModal = useJobDetailStore((state) => state.closeWorkerModal);

  // Pre-fetch messaging conversations to show unread count in tab header when job loads
  const { data: conversationsData, refetch: refetchConversations } = useQuery({
    queryKey: [QueryKeys.MESSAGE_CONVERSATIONS, job?.id],
    queryFn: () => chatApi.fetchConversations(job?.id, null),
    enabled: job?.id !== undefined,
  });

  useEffect(() => {
    const totalUnread =
      conversationsData?.results?.reduce(
        (sum, conv) => sum + (conv.unreadCount ?? 0),
        0
      ) ?? 0;
    setMessageUnreadCount(totalUnread);
  }, [conversationsData]);

  // Handle URL path parameter changes for tab switching
  useEffect(() => {
    if (activeTab) {
      // Check if activeTab is a valid tab key from our enum
      const isValidTab = Object.values(OverviewTabs).includes(
        activeTab as OverviewTabs
      );
      if (isValidTab) {
        setKey(activeTab);
        setIsTabValidated(true);
      } else {
        // Redirect to default tab if invalid
        navigate(`${BASE_PATH}jobs/${id}/${OverviewTabs.DETAILS}`);
      }
    } else {
      // No tab in URL, use default tab without redirecting
      setKey(OverviewTabs.DETAILS);
      setIsTabValidated(true);
    }
  }, [activeTab, id, navigate]);

  // Handle URL query parameter changes for tab switching (backward compatibility)
  useEffect(() => {
    const activeTabParam = searchParams.get('activeTab');
    if (activeTabParam && !activeTab) {
      const isValidTab = Object.values(OverviewTabs).includes(
        activeTabParam as OverviewTabs
      );
      if (isValidTab) {
        navigate(`${BASE_PATH}jobs/${id}/${activeTabParam}`);
      }
    }
  }, [searchParams, activeTab, id, navigate]);

  // Remove the old popstate handler as we now have proper URL-based navigation
  // The browser's back/forward buttons will work naturally with the URL structure

  if (id === undefined) {
    return <ThError message="No Job Id Found" />;
  }

  if (isLoading) return <LoaderJobDetail />;

  if (error) {
    if (error.response?.status) {
      return <>No data found</>;
    }
    return <ThError message={error.message} />;
  }

  // Don't render tabs until we've validated the tab parameter
  if (!isTabValidated) {
    return <LoaderJobDetail />;
  }

  const jobTabs = [
    {
      name: OverviewTabs.DETAILS,
      description: 'Job Details',
      component: ENABLE_SUGGESTED_WORKERS ? <DetailV2 /> : <Detail />,
    },
    {
      name: OverviewTabs.SEARCH_TALENTS,
      description: 'Search Talent',
      component: <SearchTalent />,
    },
    {
      name: OverviewTabs.SCHEDULING,
      description: 'Scheduling',
      component: <Scheduling />,
    },
    {
      name: OverviewTabs.ACTIVITY_TRACKER,
      description: `Activity Tracker (${activitiesCount})`,
      component: <ActivityTracker setActivitiesCount={setActivitiesCount} />,
    },
    {
      name: OverviewTabs.MESSAGING,
      description: `Messaging (${messageUnreadCount})`,
      component: <Messaging setMessageUnreadCount={setMessageUnreadCount} />,
    },
    {
      name: OverviewTabs.PAYROLL_SERVICES,
      description: 'Payroll Services',
      component: <PayrollServices isActive={key === 'payrollServices'} />,
    },
  ];

  const selectTab = (tabName: string | null) => {
    if (!tabName) return;
    setKey(tabName);
    // Navigate to the new URL with the tab parameter
    navigate(`${BASE_PATH}jobs/${id}/${tabName}`);
    // Refetch messaging unread count when switching tabs
    refetchConversations();
  };

  return (
    <>
      {isMobile ? (
        <div>
          <Dropdown>
            <Dropdown.Toggle variant="" className={isMobile ? 'ms-3 mt-2' : ''}>
              <Stack direction="horizontal" gap={2}>
                <b>
                  {jobTabs.find((tab) => tab.name === key)?.description ||
                    'Tab'}
                </b>
                <FiChevronDown size={24} />
              </Stack>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {jobTabs.map((tab) => (
                <Dropdown.Item
                  key={tab.name}
                  onClick={() => selectTab(tab.name)}
                >
                  {tab.description}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Body>{jobTabs.find((tab) => tab.name === key)?.component}</Body>
        </div>
      ) : (
        <Tabs
          id="controlled-tab-example"
          className="job-header"
          fill
          activeKey={key}
          onSelect={selectTab}
          mountOnEnter
        >
          {jobTabs.map((tab) => (
            <Tab key={tab.name} eventKey={tab.name} title={tab.description}>
              <Body>{tab.component}</Body>
            </Tab>
          ))}
        </Tabs>
      )}
      {!!chatPerson && (
        <SideEndModal
          show={!!chatPerson}
          width={isMobile ? '90%' : '70%'}
          onHide={clearChatData}
        >
          <JobMessageChat
            showWorkerInfo={showWorkerPrivateInfo}
            worker={chatPerson}
            onClose={clearChatData}
            onViewProfile={() => openWorkerModal(chatPerson.id)}
          />
        </SideEndModal>
      )}
      {selectedWorkerId && job && (
        <WorkerModal
          workerId={selectedWorkerId}
          jobId={job.id}
          show={!!selectedWorkerId}
          onClose={closeWorkerModal}
          showJobDetails
          zIndex={chatPerson ? 1100 : undefined}
        />
      )}
    </>
  );
}
